import { PermissionRequest, Settings, SetUserRole, UserRole, } from '@streamr/tv-engine';
import { areEqualBinaries, binaryToHex, binaryToUtf8, toUserId, toUserIdRaw, utf8ToBinary, } from '@streamr/utils';
import { EventEmitter } from 'eventemitter3';
import { decrypt, encrypt } from '../helpers/EcdhEncryption';
import { UserStore } from './UserStore';
const ONE_TO_ONE_STREAM_DOMAIN = 'tv.streamr.eth';
function generateOneToOneStreamId() {
    const randomPart = Math.random().toString(36).substring(2);
    return `${ONE_TO_ONE_STREAM_DOMAIN}/1to1-${randomPart}`;
}
function generateOneToOneAesKey() {
    return crypto.getRandomValues(new Uint8Array(32));
}
function buildOneToOneStreamUrl(streamId, aesKey) {
    return `${streamId}#${binaryToHex(aesKey, false)}`;
}
function truncateUserId(userId) {
    if (userId.length <= 10) {
        return userId;
    }
    return `${userId.slice(0, 6)}...${userId.slice(-4)}`;
}
export class UserManager extends EventEmitter {
    hostUserId;
    ownUserId;
    ownPrivateKey;
    users = new Map();
    outgoingRequests = new Set();
    usersByStatus = new Map([
        [UserRole.VIEWER, new Set()],
        [UserRole.SPEAKER, new Set()],
        [UserRole.COHOST, new Set()],
        [UserRole.GUEST, new Set()],
        [UserRole.CAMERA, new Set()],
    ]);
    userStore;
    heartbeatDisplayNames = new Map();
    localDisplayName;
    ownStatus = UserRole.VIEWER;
    speakerSecret;
    hostSecret;
    streamrWrapper;
    heartbeatMonitorInterval;
    pendingSecretRequest;
    constructor(hostUserId, ownUserId, ownPrivateKey, speakerSecret, hostSecret) {
        super();
        this.hostUserId = hostUserId;
        this.ownUserId = ownUserId;
        this.ownPrivateKey = ownPrivateKey;
        this.userStore = new UserStore();
        this.localDisplayName = this.userStore.getDisplayName();
        if (this.isHost(this.ownUserId)) {
            this.speakerSecret = crypto.getRandomValues(new Uint8Array(32));
            this.hostSecret = crypto.getRandomValues(new Uint8Array(32));
            this.ownStatus = UserRole.COHOST;
            this.emitOwnRoleChanged(UserRole.COHOST);
        }
        else {
            this.speakerSecret = speakerSecret;
            this.hostSecret = hostSecret;
            const persistedRole = this.userStore.getUserRole(this.hostUserId);
            if (persistedRole !== undefined &&
                persistedRole !== UserRole.VIEWER &&
                speakerSecret) {
                this.ownStatus = persistedRole;
                const storedOneToOneUrl = this.userStore.getOneToOneStreamUrl(this.hostUserId);
                console.log('[UserManager] Restoring persisted role from localStorage', {
                    persistedRole,
                    hasSpeakerSecret: !!speakerSecret,
                    storedOneToOneUrl: storedOneToOneUrl?.slice(0, 30),
                });
                this.emitOwnRoleChanged(persistedRole, storedOneToOneUrl);
            }
            else if (hostSecret) {
                void this.requestPermission(UserRole.COHOST);
            }
            else if (speakerSecret) {
                void this.requestPermission(UserRole.SPEAKER);
            }
        }
    }
    // --- User map helpers ---
    getOrCreateUser(userId) {
        if (userId === this.ownUserId) {
            throw new Error('Local user must not be added to the users map');
        }
        let user = this.users.get(userId);
        if (!user) {
            user = {
                id: userId,
                displayName: undefined,
                status: UserRole.VIEWER,
                online: true,
                incomingRequest: undefined,
                lastHeartbeatTimeMillis: Date.now(),
                uploadRateBytesPerSecond: 0,
                downloadRateBytesPerSecond: 0,
                neighborCount: 0,
                isRecording: false,
            };
            this.users.set(userId, user);
            this.usersByStatus.get(UserRole.VIEWER)?.add(userId);
            this.emit('userCameOnline', userId, user.displayName ?? userId);
        }
        else if (!user.online) {
            user.online = true;
            user.lastHeartbeatTimeMillis = Date.now();
            this.emit('userCameOnline', userId, user.displayName ?? userId);
        }
        return user;
    }
    changeUserRole(userId, newStatus) {
        const user = this.users.get(userId);
        if (!user) {
            return;
        }
        const oldStatus = user.status;
        if (oldStatus === newStatus) {
            return;
        }
        this.usersByStatus.get(oldStatus)?.delete(userId);
        this.usersByStatus.get(newStatus)?.add(userId);
        user.status = newStatus;
        this.emit('userRoleChanged', userId, newStatus, user.displayName ?? userId);
    }
    handleUserDisconnect(userId) {
        const user = this.users.get(userId);
        if (!user) {
            return;
        }
        const hasElevatedRole = user.status !== UserRole.VIEWER && user.status !== UserRole.BANNED;
        if (hasElevatedRole) {
            user.online = false;
            this.emit('userWentOffline', userId);
            return;
        }
        this.usersByStatus.get(user.status)?.delete(userId);
        this.users.delete(userId);
        this.emit('userWentOffline', userId);
        this.emit('userCountChanged', this.users.size);
        if (user.displayName !== undefined) {
            this.emitDisplayNamesChanged();
        }
        if (user.incomingRequest !== undefined) {
            this.emitIncomingRequestsChanged();
        }
    }
    // --- Display name management ---
    setLocalDisplayName(name) {
        this.localDisplayName = name;
        this.userStore.setDisplayName(name);
        this.emitDisplayNamesChanged();
    }
    getLocalDisplayName() {
        return this.localDisplayName;
    }
    updateRemoteDisplayName(userId, displayName) {
        if (userId === this.ownUserId) {
            return;
        }
        const user = this.users.get(userId);
        if (user) {
            if (user.displayName !== displayName) {
                user.displayName = displayName;
                this.emitDisplayNamesChanged();
            }
        }
    }
    resolveDisplayName(userId) {
        if (userId === this.ownUserId) {
            return this.localDisplayName || truncateUserId(userId);
        }
        const user = this.users.get(userId);
        return (user?.displayName ||
            this.heartbeatDisplayNames.get(userId) ||
            truncateUserId(userId));
    }
    getDisplayNames() {
        const result = new Map();
        for (const [userId, name] of this.heartbeatDisplayNames) {
            result.set(userId, name);
        }
        for (const user of this.users.values()) {
            if (user.displayName !== undefined) {
                result.set(user.id, user.displayName);
            }
        }
        if (this.localDisplayName) {
            result.set(this.ownUserId, this.localDisplayName);
        }
        return result;
    }
    emitDisplayNamesChanged() {
        this.emit('displayNamesChanged', this.getDisplayNames());
    }
    // --- Heartbeat integration ---
    handleHeartbeat(publisherId, heartbeat) {
        if (publisherId === this.ownUserId) {
            return;
        }
        if (heartbeat.displayName) {
            const prev = this.heartbeatDisplayNames.get(publisherId);
            if (prev !== heartbeat.displayName) {
                this.heartbeatDisplayNames.set(publisherId, heartbeat.displayName);
                this.emitDisplayNamesChanged();
            }
        }
        if (this.pendingSecretRequest !== undefined) {
            const pendingStatus = this.pendingSecretRequest;
            this.pendingSecretRequest = undefined;
            void this.requestPermission(pendingStatus);
        }
        const existingUser = this.users.get(publisherId);
        if (existingUser) {
            existingUser.lastHeartbeatTimeMillis = Date.now();
            existingUser.uploadRateBytesPerSecond =
                heartbeat.uploadRateBytesPerSecond;
            existingUser.downloadRateBytesPerSecond =
                heartbeat.downloadRateBytesPerSecond;
            existingUser.neighborCount = heartbeat.neighbors.length;
            existingUser.isRecording = heartbeat.isRecording;
            return;
        }
        const isHostOrCohost = this.ownStatus === UserRole.COHOST || this.isHost(this.ownUserId);
        if (!isHostOrCohost) {
            return;
        }
        const user = this.getOrCreateUser(publisherId);
        user.uploadRateBytesPerSecond = heartbeat.uploadRateBytesPerSecond;
        user.downloadRateBytesPerSecond = heartbeat.downloadRateBytesPerSecond;
        user.neighborCount = heartbeat.neighbors.length;
        user.isRecording = heartbeat.isRecording;
        this.emit('userCountChanged', this.users.size);
    }
    startHeartbeatMonitor() {
        if (this.heartbeatMonitorInterval) {
            return;
        }
        this.heartbeatMonitorInterval = setInterval(() => {
            const now = Date.now();
            for (const [userId, user] of this.users.entries()) {
                const hasElevatedRole = user.status === UserRole.SPEAKER ||
                    user.status === UserRole.COHOST ||
                    user.status === UserRole.GUEST ||
                    user.status === UserRole.CAMERA;
                const expirationMs = hasElevatedRole
                    ? Settings.speakerHeartbeatInterval
                    : Settings.defaultHeartbeatInterval;
                if (user.online &&
                    now - user.lastHeartbeatTimeMillis > expirationMs) {
                    this.handleUserDisconnect(userId);
                }
            }
        }, Settings.viewerCounterUpdateInterval);
    }
    stopHeartbeatMonitor() {
        if (this.heartbeatMonitorInterval) {
            clearInterval(this.heartbeatMonitorInterval);
            this.heartbeatMonitorInterval = undefined;
        }
    }
    // --- ViewerCounter-equivalent methods (host/cohost only) ---
    getViewerCount() {
        return this.users.size;
    }
    getUploadRates() {
        return Array.from(this.users.values()).map((u) => u.uploadRateBytesPerSecond);
    }
    getDownloadRates() {
        return Array.from(this.users.values()).map((u) => u.downloadRateBytesPerSecond);
    }
    getNeighborCounts() {
        return Array.from(this.users.values()).map((u) => u.neighborCount);
    }
    // --- Permission management ---
    isHost(userId) {
        return userId === this.hostUserId;
    }
    isHostOrCohost(userId) {
        if (userId === this.ownUserId) {
            return this.isHost(userId) || this.ownStatus === UserRole.COHOST;
        }
        const user = this.users.get(userId);
        return (this.isHost(userId) ||
            (user !== undefined && user.status === UserRole.COHOST));
    }
    getSecretForStatus(status) {
        return status === UserRole.SPEAKER
            ? this.speakerSecret
            : this.hostSecret;
    }
    async encryptForUser(userId, data) {
        return await encrypt(data, toUserIdRaw(userId));
    }
    requestKey(userId, status) {
        return `${userId}:${status}`;
    }
    emitOwnRoleChanged(userRole, oneToOneStreamUrl) {
        this.emit('ownRoleChanged', userRole, oneToOneStreamUrl);
    }
    emitIncomingRequestsChanged() {
        const requests = [];
        for (const user of this.users.values()) {
            if (user.incomingRequest !== undefined) {
                requests.push({
                    userId: user.id,
                    userRole: user.incomingRequest,
                });
            }
        }
        this.emit('incomingRequestsChanged', requests);
    }
    emitOutgoingRequestsChanged() {
        const requests = [];
        for (const requestKey of this.outgoingRequests) {
            const [userIdStr, statusStr] = requestKey.split(':');
            const userId = userIdStr;
            const userRole = Number.parseInt(statusStr, 10);
            requests.push({ userId, userRole });
        }
        this.emit('outgoingRequestsChanged', requests);
    }
    getCurrentUserRole() {
        return this.ownStatus;
    }
    getCurrentUserId() {
        return this.ownUserId;
    }
    getIncomingRequests() {
        const requests = [];
        for (const user of this.users.values()) {
            if (user.incomingRequest !== undefined) {
                requests.push({
                    userId: user.id,
                    userRole: user.incomingRequest,
                });
            }
        }
        return requests;
    }
    getStreamrWrapper() {
        return this.streamrWrapper;
    }
    async requestPermission(userRole) {
        const secret = this.getSecretForStatus(userRole);
        this.outgoingRequests.add(this.requestKey(this.ownUserId, userRole));
        this.emitOutgoingRequestsChanged();
        this.streamrWrapper?.publishPermissionRequest(PermissionRequest.create({
            userRole,
            secret: secret
                ? await this.encryptForUser(this.hostUserId, secret)
                : undefined,
            displayName: this.localDisplayName,
        }));
    }
    async acceptPermission(userId, permission) {
        const user = this.getOrCreateUser(userId);
        if (user.status === permission) {
            return;
        }
        const userIdRaw = toUserIdRaw(userId);
        this.changeUserRole(userId, permission);
        user.incomingRequest = undefined;
        this.emitIncomingRequestsChanged();
        if (permission === UserRole.COHOST &&
            this.hostSecret &&
            this.speakerSecret) {
            this.streamrWrapper?.publishSetUserRole(SetUserRole.create({
                targetUserId: userIdRaw,
                userRole: permission,
                hostSecret: await this.encryptForUser(userId, this.hostSecret),
                speakerSecret: await this.encryptForUser(userId, this.speakerSecret),
            }));
        }
        else if (permission === UserRole.GUEST && this.speakerSecret) {
            const oneToOneStreamId = generateOneToOneStreamId();
            const oneToOneAesKey = generateOneToOneAesKey();
            const oneToOneStreamUrl = buildOneToOneStreamUrl(oneToOneStreamId, oneToOneAesKey);
            this.streamrWrapper?.publishSetUserRole(SetUserRole.create({
                targetUserId: userIdRaw,
                userRole: permission,
                speakerSecret: await this.encryptForUser(userId, this.speakerSecret),
                oneToOneStreamUrl,
            }));
            this.emit('oneToOneStreamCreated', userId, oneToOneStreamUrl);
        }
        else if (permission === UserRole.SPEAKER && this.speakerSecret) {
            this.streamrWrapper?.publishSetUserRole(SetUserRole.create({
                targetUserId: userIdRaw,
                userRole: permission,
                speakerSecret: await this.encryptForUser(userId, this.speakerSecret),
            }));
        }
    }
    rejectPermission(userId, _permission) {
        const user = this.users.get(userId);
        if (user) {
            user.incomingRequest = undefined;
        }
        this.emitIncomingRequestsChanged();
        this.streamrWrapper?.publishSetUserRole(SetUserRole.create({
            targetUserId: toUserIdRaw(userId),
            userRole: UserRole.VIEWER,
        }));
    }
    leaveStage() {
        if (this.ownStatus === UserRole.VIEWER) {
            return;
        }
        this.speakerSecret = undefined;
        this.hostSecret = undefined;
        this.ownStatus = UserRole.VIEWER;
        this.userStore.clearSpeakerSecret(this.hostUserId);
        this.userStore.clearUserRole(this.hostUserId);
        this.userStore.clearOneToOneStreamUrl(this.hostUserId);
        this.emitOwnRoleChanged(UserRole.VIEWER);
        this.streamrWrapper?.publishSetUserRole(SetUserRole.create({
            targetUserId: toUserIdRaw(this.ownUserId),
            userRole: UserRole.VIEWER,
        }));
    }
    revokePermission(userId, _permission) {
        if (!this.hostSecret) {
            return;
        }
        this.changeUserRole(userId, UserRole.VIEWER);
        this.streamrWrapper?.publishSetUserRole(SetUserRole.create({
            targetUserId: toUserIdRaw(userId),
            userRole: UserRole.VIEWER,
        }));
    }
    async sendPrivateMessage(targetUserId, message) {
        const binaryMessage = utf8ToBinary(message);
        const encryptedMessage = await encrypt(binaryMessage, toUserIdRaw(targetUserId));
        this.streamrWrapper?.publishPrivateMessage({
            data: encryptedMessage,
            targetUserId: toUserIdRaw(targetUserId),
        });
        this.emit('privateChatMessage', {
            senderId: targetUserId,
            message,
            timestamp: Date.now(),
            isSent: true,
        });
    }
    setStreamrWrapper(streamrWrapper) {
        this.streamrWrapper = streamrWrapper;
        this.streamrWrapper.on('permissionRequest', (permissionRequest, publisherId) => {
            void this.onPermissionRequest(toUserId(publisherId), permissionRequest.userRole, permissionRequest.secret, permissionRequest.displayName);
        });
        this.streamrWrapper.on('setUserRole', (setUserRole) => {
            void this.onSetUserRole(toUserId(setUserRole.targetUserId), setUserRole.userRole, setUserRole.speakerSecret, setUserRole.hostSecret, setUserRole.oneToOneStreamUrl);
        });
        this.streamrWrapper.on('privateMessage', (privateMessage, publisherId) => {
            if (areEqualBinaries(privateMessage.targetUserId, toUserIdRaw(this.ownUserId))) {
                void this.onPrivateMessage(toUserId(publisherId), privateMessage.data);
            }
        });
        this.streamrWrapper.on('leave', (_leave, publisherId) => {
            this.onLeave(toUserId(publisherId));
        });
        this.startHeartbeatMonitor();
        if (!this.isHost(this.ownUserId) &&
            this.ownStatus === UserRole.VIEWER) {
            if (this.hostSecret) {
                this.pendingSecretRequest = UserRole.COHOST;
            }
            else if (this.speakerSecret) {
                this.pendingSecretRequest = UserRole.SPEAKER;
            }
        }
    }
    async onPermissionRequest(senderId, userRole, secret, displayName) {
        let autoAccepted = false;
        if (secret && this.speakerSecret && this.hostSecret) {
            const validationSecret = this.getSecretForStatus(userRole);
            if (!validationSecret) {
                throw new Error('Assertion error: validationSecret is undefined');
            }
            try {
                const decryptedSecret = await decrypt(this.ownPrivateKey, secret);
                const isValid = areEqualBinaries(decryptedSecret, validationSecret);
                if (isValid) {
                    await this.acceptPermission(senderId, userRole);
                    autoAccepted = true;
                }
                else {
                    this.rejectPermission(senderId, userRole);
                }
            }
            catch (_e) { }
        }
        if (!autoAccepted && this.isHostOrCohost(this.ownUserId)) {
            const user = this.getOrCreateUser(senderId);
            if (displayName) {
                this.updateRemoteDisplayName(senderId, displayName);
            }
            user.incomingRequest = userRole;
            this.emitIncomingRequestsChanged();
        }
    }
    async onSetUserRole(targetUserId, userRole, speakerSecret, hostSecret, oneToOneStreamUrl) {
        if (targetUserId === this.ownUserId) {
            this.outgoingRequests.delete(this.requestKey(this.ownUserId, UserRole.SPEAKER));
            this.outgoingRequests.delete(this.requestKey(this.ownUserId, UserRole.COHOST));
            this.outgoingRequests.delete(this.requestKey(this.ownUserId, UserRole.GUEST));
            this.emitOutgoingRequestsChanged();
            if (userRole === UserRole.VIEWER) {
                this.speakerSecret = undefined;
                this.hostSecret = undefined;
                this.ownStatus = UserRole.VIEWER;
                this.userStore.clearSpeakerSecret(this.hostUserId);
                this.userStore.clearUserRole(this.hostUserId);
                this.userStore.clearOneToOneStreamUrl(this.hostUserId);
                this.emitOwnRoleChanged(UserRole.VIEWER);
            }
            else {
                try {
                    if (speakerSecret) {
                        this.speakerSecret = await decrypt(this.ownPrivateKey, speakerSecret);
                        this.userStore.setSpeakerSecret(this.hostUserId, this.speakerSecret);
                    }
                    if (hostSecret) {
                        this.hostSecret = await decrypt(this.ownPrivateKey, hostSecret);
                    }
                }
                catch (error) {
                    console.error('[UserManager] Failed to decrypt secrets in onSetUserRole:', error);
                    return;
                }
                this.ownStatus = userRole;
                this.userStore.setUserRole(this.hostUserId, userRole);
                if (oneToOneStreamUrl) {
                    this.userStore.setOneToOneStreamUrl(this.hostUserId, oneToOneStreamUrl);
                }
                this.emitOwnRoleChanged(userRole, oneToOneStreamUrl);
            }
        }
        else {
            if (userRole === UserRole.VIEWER) {
                const user = this.users.get(targetUserId);
                if (user) {
                    this.changeUserRole(targetUserId, UserRole.VIEWER);
                }
            }
            else {
                this.getOrCreateUser(targetUserId);
                this.changeUserRole(targetUserId, userRole);
            }
        }
    }
    async onPrivateMessage(senderId, data) {
        let decryptedMessage;
        try {
            decryptedMessage = await decrypt(this.ownPrivateKey, data);
        }
        catch (error) {
            console.error('[UserManager] Failed to decrypt private message from', senderId, error);
            return;
        }
        const message = binaryToUtf8(decryptedMessage);
        const chatMessage = {
            senderId,
            message,
            timestamp: Date.now(),
        };
        this.emit('privateChatMessage', chatMessage);
    }
    onLeave(userId) {
        this.handleUserDisconnect(userId);
    }
    getSpeakers() {
        const result = Array.from(this.usersByStatus.get(UserRole.SPEAKER) ?? []);
        if (this.ownStatus === UserRole.SPEAKER) {
            result.push(this.ownUserId);
        }
        return result;
    }
    getCohosts() {
        const result = Array.from(this.usersByStatus.get(UserRole.COHOST) ?? []);
        if (this.ownStatus === UserRole.COHOST &&
            !this.isHost(this.ownUserId)) {
            result.push(this.ownUserId);
        }
        return result;
    }
    getSpeakerRequests() {
        const result = [];
        for (const user of this.users.values()) {
            if (user.incomingRequest === UserRole.SPEAKER) {
                result.push(user.id);
            }
        }
        return result;
    }
    getCohostRequests() {
        const result = [];
        for (const user of this.users.values()) {
            if (user.incomingRequest === UserRole.COHOST) {
                result.push(user.id);
            }
        }
        return result;
    }
    getGuests() {
        const result = Array.from(this.usersByStatus.get(UserRole.GUEST) ?? []);
        if (this.ownStatus === UserRole.GUEST) {
            result.push(this.ownUserId);
        }
        return result;
    }
    getCameras() {
        return Array.from(this.usersByStatus.get(UserRole.CAMERA) ?? []);
    }
    getGuestRequests() {
        const result = [];
        for (const user of this.users.values()) {
            if (user.incomingRequest === UserRole.GUEST) {
                result.push(user.id);
            }
        }
        return result;
    }
    getOutgoingRequests() {
        return Array.from(this.outgoingRequests).map((key) => {
            const [userIdStr, statusStr] = key.split(':');
            const userId = userIdStr;
            const userRole = Number.parseInt(statusStr, 10);
            return { userId, userRole };
        });
    }
    getUser(userId) {
        return this.users.get(userId);
    }
    getUsers() {
        return this.users;
    }
}
